import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Ticket, Loader2 } from "lucide-react";

const KEY = "promo_splash_dismissed_at";
const COOLDOWN_MS = 24 * 60 * 60 * 1000;

interface Promo {
  code: string;
  homepage_headline: string | null;
  homepage_subtext: string | null;
  duration_days: number;
  tier: string;
}

export default function PromoSplashPopup() {
  const [open, setOpen] = useState(false);
  const [promo, setPromo] = useState<Promo | null>(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const last = Number(localStorage.getItem(KEY) || 0);
    if (Date.now() - last < COOLDOWN_MS) return;
    const t = setTimeout(async () => {
      const { data, error } = await (supabase as any).rpc("get_active_homepage_promo");
      if (error || !data || data.length === 0) return;
      setPromo(data[0]);
      setOpen(true);
    }, 4000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(KEY, String(Date.now()));
    setOpen(false);
  };

  const submit = async () => {
    if (!promo || !email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    setSubmitting(true);
    try {
      await supabase.functions.invoke("send-promo-code-email", {
        body: {
          email,
          code: promo.code,
          headline: promo.homepage_headline,
          duration_days: promo.duration_days,
          tier: promo.tier,
        },
      });
      setSent(true);
      localStorage.setItem(KEY, String(Date.now()));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && promo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={dismiss}
        >
          <motion.div
            initial={{ y: 30, scale: 0.95 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 20, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl bg-card shadow-2xl overflow-hidden border"
          >
            <button onClick={dismiss} className="absolute right-3 top-3 z-10 rounded-full p-1.5 bg-background/70 hover:bg-background">
              <X className="h-4 w-4" />
            </button>
            <div className="bg-gradient-to-br from-primary to-primary/70 p-6 text-primary-foreground">
              <div className="flex items-center gap-2 mb-2"><Ticket className="h-5 w-5" /><span className="text-xs uppercase tracking-wide opacity-90">Limited offer</span></div>
              <h2 className="text-2xl font-bold">{promo.homepage_headline || "Unlock free access"}</h2>
              <p className="text-sm opacity-90 mt-1">{promo.homepage_subtext || `${promo.duration_days} days of ${promo.tier.toUpperCase()}, on us.`}</p>
            </div>
            <div className="p-6 space-y-4">
              {sent ? (
                <div className="text-center space-y-2">
                  <p className="text-sm">Your code:</p>
                  <code className="block font-mono text-2xl font-bold tracking-wider text-primary">{promo.code}</code>
                  <p className="text-xs text-muted-foreground">We've also emailed it to you. Apply it in Settings → Subscription.</p>
                  <Button onClick={dismiss} className="w-full mt-2">Got it</Button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">Enter your email and we'll send you the code.</p>
                  <Input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                  <Button onClick={submit} disabled={submitting || !email} className="w-full">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send me the code"}
                  </Button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}